import { defineStore, storeToRefs } from 'pinia';
import { fetchWrapper } from '@/helpers';
import { useAccountStore, useAlertStore } from '.';
import { updateLicenses } from './account.store';

const baseUrl = `${process.env.VUE_APP_VUE_API_URL}`;
export const useProxyStore = defineStore('proxy', {
    state: () => ({
        proxyList: [],
        loading: false,
    }),
    actions: {
        async getProxyList() {
            const alertStore = useAlertStore();
            try {
                const res = await fetchWrapper.post(`${baseUrl}/proxy/my-list`);
                this.proxyList = res.proxyList;
                updateLicenses()
            }
            catch (error) {
                alertStore.error(error);
            }
            return
        },
        async updateAutoRotationTime(uuid, minute) {
            const alertStore = useAlertStore()
            try {
                await fetchWrapper.post(`${baseUrl}/license/change-rotation-time`, { uuid, minute: minute === 0 ? -1 : minute });
                alertStore.success("You set successfully")
                return
            } catch (error) {
                alertStore.error(error)
            }
            return

        },
        async updateAutoExtend(uuid, extend_flag) {
            const alertStore = useAlertStore()
            try {
                await fetchWrapper.post(`${baseUrl}/license/update-auto-extend`, { uuid, auto_extend: extend_flag });
                return true
            } catch (error) {
                alertStore.error(error)
            }
            return false

        },
        async updateLicense(uuid) {
            const alertStore = useAlertStore()
            try {
                const res = await fetchWrapper.post(`${baseUrl}/license/update-license`, { uuid });
                this.proxyList = this.proxyList.map(proxy => {
                    if(proxy.uuid === res.previous) {
                        proxy.uuid = res.newUuid
                    }
                    return proxy
                })
                alertStore.success("Changed IP link successfully.")
            } catch (error) {
                alertStore.error(error)
            }
            return false

        },
        async rebootDevice(license) {
            const alertStore = useAlertStore()
            try {
                this.loading = true;
                const res = await fetchWrapper.post(`${baseUrl}/proxy/reboot-device`, { license });
                this.loading = false;
                alertStore.success(res.message)
            } catch (error) {
                this.loading = false;
                alertStore.error(error)
            }
            return

        },
        async updateUserAuthentication(license, username, ipAddress) {
            const alertStore = useAlertStore()
            try {
                const res = await fetchWrapper.post(`${baseUrl}/proxy/update-user-authentication`, { license, username, ipAddress });
                // this.proxyList = this.proxyList.map(proxy=>{
                //     if(proxy.uuid===license){
                //         return res.proxy
                //     }
                //     else{
                //         return proxy
                //     }
                // });
                updateLicenses()
                alertStore.success("Updated Auth")

            } catch (error) {
                alertStore.error(error)
            }
            return
        },
        async updateNote(license, note) {
            const alertStore = useAlertStore()
            try {
                await fetchWrapper.post(`${baseUrl}/proxy/update-note`, { license, note });
                updateLicenses()
                alertStore.success("Updated Note")

            } catch (error) {
                alertStore.error(error)
            }
            return
        },
        async updateCoupon(license, couponCode) {
            const alertStore = useAlertStore()
            try {
                await fetchWrapper.post(`${baseUrl}/proxy/update-coupon`, { license, couponCode });
                updateLicenses()
                alertStore.success("Updated Note")

            } catch (error) {
                alertStore.error(error)
            }
            return
        },
        // ---- ProProxyUI: VPN (WireGuard / OpenVPN, always ready on the box) and OS profiles ----
        async getVpn(uuid) {
            try { return await fetchWrapper.get(`${baseUrl}/proxy/vpn?license=${encodeURIComponent(uuid)}`); }
            catch (error) { useAlertStore().error(error); return null }
        },
        // background refresh of the VPN window: no error pop-up if a server is slow
        async getVpnQuiet(uuid) {
            try { return await fetchWrapper.get(`${baseUrl}/proxy/vpn?license=${encodeURIComponent(uuid)}`); }
            catch (error) { return null }
        },
        async enableVpn(uuid, type) {   // type 'wg-new-keys': a fresh WireGuard file, the old one stops working
            try { return await fetchWrapper.post(`${baseUrl}/proxy/vpn/enable`, { uuid, type }); }
            catch (error) { useAlertStore().error(error); return null }
        },
        async vpnQrLink(uuid) {   // a 10-minute .ovpn link for the OpenVPN QR code
            try { return await fetchWrapper.post(`${baseUrl}/proxy/vpn/qr-link`, { uuid }); }
            catch (error) { useAlertStore().error(error); return null }
        },
        async vpnFile(uuid, type) {   // type: 'wg' or 'ovpn' -> { filename, content, type }
            try { return await fetchWrapper.get(`${baseUrl}/proxy/vpn/file?license=${encodeURIComponent(uuid)}${type ? '&type=' + encodeURIComponent(type) : ''}`); }
            catch (error) { useAlertStore().error(error); return null }
        },
        // live VPN state of the ProProxyUI licences on My Proxies, merged into the list as `vpn`
        // (quiet: a slow box must not pop up an error every 30 s)
        async refreshVpnLive() {
            if (!this.proxyList.some(p => p.m_type === 'PROPROXYUI' && p.status === 'ACTIVE')) return
            try {
                const res = await fetchWrapper.get(`${baseUrl}/proxy/vpn-live`);
                const states = (res && res.states) || {}
                this.proxyList = this.proxyList.map(p => (p.m_type === 'PROPROXYUI' && states[p.uuid]) ? { ...p, vpn: states[p.uuid] } : p)
            } catch (error) { /* the last known state stays */ }
        },
        // IP change history of a ProProxyUI licence (the modem's rotation log since the licence started), newest first
        async getIpHistory(uuid) {
            try {
                const res = await fetchWrapper.get(`${baseUrl}/proxy/ip-history?license=${encodeURIComponent(uuid)}`);
                return (res && res.history) || []
            } catch (error) {
                useAlertStore().error(error)
                return null
            }
        },
        async getOsOptions(uuid) {
            try {
                if (!this._osOptionsCache) this._osOptionsCache = {}
                if (this._osOptionsCache[uuid]) return this._osOptionsCache[uuid]
                const res = await fetchWrapper.get(`${baseUrl}/proxy/os-options?license=${encodeURIComponent(uuid)}`);
                if (res && res.options) this._osOptionsCache[uuid] = res
                return res;
            } catch (error) {
                useAlertStore().error(error)
                return null
            }
        },
        async changeOS(uuid, osType, mtu) {
            const alertStore = useAlertStore()
            try {
                // mtu: ProProxyUI only (0 = match the fingerprint)
                await fetchWrapper.post(`${baseUrl}/proxy/change-os`, { uuid, osType, ...(mtu !== undefined ? { mtu } : {}) });
                if (this._osOptionsCache) delete this._osOptionsCache[uuid]   // the window shows the new choice next time
                alertStore.success("Updated OS Spoofing")

            } catch (error) {
                alertStore.error(error)
            }
            return
        },
        async extendLicense(license, time_unit) {
            const alertStore = useAlertStore()
            try {
                const res = await fetchWrapper.post(`${baseUrl}/license/extend`, { license, time_unit });
                // this.proxyList = this.proxyList.map(proxy=>{
                //     if(proxy.uuid===license){
                //         return res.proxy
                //     }
                //     else{
                //         return proxy
                //     }
                // });
                const accountStore = useAccountStore()
                const account = storeToRefs(accountStore)
                account.user.value.balance = res.balance
                updateLicenses()
                alertStore.success("Extend Success")
            } catch (error) {
                alertStore.error(error)
            }
            return

        },
        async changeIp(license) {
            const alertStore = useAlertStore()
            try {
                await fetchWrapper.get(`${baseUrl}/proxy/change-ip?license=${license}`);
                return
            } catch (error) {
                alertStore.error(error)
            }
            return

        },
        async refreshIp(license) {
            const alertStore = useAlertStore()
            try {
                const res = await fetchWrapper.get(`${baseUrl}/proxy/refresh-ip?license=${license}`);
                return res.publicIp
            } catch (error) {
                alertStore.error(error)
            }
            return

        },

        // -------------------------
        // Move Location (ProxySmart only)
        // -------------------------
        async getMoveLocationModems(license_uuid) {
            const alertStore = useAlertStore()
            try {
                const res = await fetchWrapper.post(`${baseUrl}/proxy/move-location/modems`, { license_uuid });
                return res?.modems || [];
            } catch (error) {
                alertStore.error(error)
                return [];
            }
        },

        async getMoveLocationProxiesAndPackages(license_uuid, modemName) {
            const alertStore = useAlertStore()
            try {
                const res = await fetchWrapper.post(`${baseUrl}/proxy/move-location/proxy-package-list`, { license_uuid, modemName });
                return { proList: res?.proxyList || [], pkgList: res?.packageList || [], modem: res?.modem || null };
            } catch (error) {
                alertStore.error(error)
                return { proList: [], pkgList: [], modem: null };
            }
        },

        async moveProxyLocation(license_uuid, modemName, proxyName, packageName) {
            const alertStore = useAlertStore()
            try {
                this.loading = true;
                const res = await fetchWrapper.post(`${baseUrl}/proxy/move-location`, { license_uuid, modemName, proxyName, packageName });
                this.loading = false;
                alertStore.success(res?.message || "Success");
                updateLicenses();
                return res;
            } catch (error) {
                this.loading = false;
                alertStore.error(error)
                return null;
            }
        }
        // Other actions...
    },
});